<template>
  <div class="bg-surface p-8 rounded-lg shadow-lg border-2 border-blue-100 dark:border-blue-800">
    <h3 class="text-2xl font-bold text-primary mb-6 text-center">Calculate Your Team's Savings</h3>
    
    <div class="grid md:grid-cols-2 gap-8">
      
      <!-- Input Controls -->
      <div class="space-y-6">
        
        <div>
          <label class="block text-sm font-semibold text-primary mb-2">
            Product Team Size
          </label>
          <div class="flex items-center space-x-4">
            <input 
              type="range" 
              min="2" 
              max="20" 
              v-model="teamSize"
              class="flex-1 h-2 bg-border rounded-lg appearance-none cursor-pointer"
            />
            <span class="w-12 text-center font-bold text-blue-600">{{ teamSize }}</span>
          </div>
        </div>

        <div>
          <label class="block text-sm font-semibold text-primary mb-2">
            Customer Calls Per Week
          </label>
          <div class="flex items-center space-x-4">
            <input 
              type="range" 
              min="5" 
              max="50" 
              step="5"
              v-model="callsPerWeek"
              class="flex-1 h-2 bg-border rounded-lg appearance-none cursor-pointer"
            />
            <span class="w-12 text-center font-bold text-blue-600">{{ callsPerWeek }}</span>
          </div>
        </div>

        <div>
          <label class="block text-sm font-semibold text-primary mb-2">
            Minutes Per Call Analysis
          </label>
          <div class="flex items-center space-x-4">
            <input 
              type="range" 
              min="15" 
              max="60" 
              step="5"
              v-model="minutesPerCall"
              class="flex-1 h-2 bg-border rounded-lg appearance-none cursor-pointer"
            />
            <span class="w-12 text-center font-bold text-blue-600">{{ minutesPerCall }}</span>
          </div>
        </div>

        <div>
          <label class="block text-sm font-semibold text-primary mb-2">
            Average Hourly Rate ($)
          </label>
          <div class="flex items-center space-x-4">
            <input 
              type="range" 
              min="50" 
              max="200" 
              step="10"
              v-model="hourlyRate"
              class="flex-1 h-2 bg-border rounded-lg appearance-none cursor-pointer"
            />
            <span class="w-16 text-center font-bold text-blue-600">${{ hourlyRate }}</span>
          </div>
        </div>

      </div>

      <!-- Results -->
      <div class="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 p-6 rounded-lg">
        <h4 class="text-lg font-bold text-primary mb-4">Your Potential Savings</h4>
        
        <div class="space-y-4">
          
          <div class="flex justify-between items-center py-2 border-b border-border">
            <span class="text-secondary">Hours saved per week:</span>
            <span class="font-bold text-xl text-blue-600">{{ hoursPerWeek }}h</span>
          </div>

          <div class="flex justify-between items-center py-2 border-b border-border">
            <span class="text-secondary">Weekly cost savings:</span>
            <span class="font-bold text-xl text-green-600">${{ weeklySavings.toLocaleString() }}</span>
          </div>

          <div class="flex justify-between items-center py-2 border-b border-border">
            <span class="text-secondary">Monthly savings:</span>
            <span class="font-bold text-xl text-green-600">${{ monthlySavings.toLocaleString() }}</span>
          </div>

          <div class="flex justify-between items-center py-3 bg-surface rounded-lg px-4 mt-4">
            <span class="text-primary font-semibold">Annual savings:</span>
            <span class="font-bold text-2xl text-green-600">${{ annualSavings.toLocaleString() }}</span>
          </div>

        </div>

        <div class="mt-6 text-sm text-secondary">
          <p class="mb-2">✅ <strong>100% insight coverage</strong> - Never miss feedback again</p>
          <p class="mb-2">✅ <strong>Zero admin overhead</strong> - Automatic extraction & categorization</p>
          <p>✅ <strong>Better prioritization</strong> - Data-driven roadmap decisions</p>
        </div>

      </div>

    </div>

    <div class="text-center mt-8">
      <p class="text-secondary mb-4">
        Ready to reclaim <strong>{{ hoursPerWeek }} hours per week</strong> for your team?
      </p>
      <NuxtLink class="btn btn-primary px-8 py-3" to="/login">
        Join Waitlist
      </NuxtLink>
    </div>

  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

// Reactive inputs
const teamSize = ref(5)
const callsPerWeek = ref(15)
const minutesPerCall = ref(30)
const hourlyRate = ref(100)

// Computed savings
const hoursPerWeek = computed(() => {
  return Math.round((callsPerWeek.value * minutesPerCall.value) / 60)
})

const weeklySavings = computed(() => {
  return Math.round(hoursPerWeek.value * hourlyRate.value)
})

const monthlySavings = computed(() => {
  return Math.round(weeklySavings.value * 4.33) // Average weeks per month
})

const annualSavings = computed(() => {
  return Math.round(weeklySavings.value * 52)
})
</script>

<style scoped>
/* Custom slider styling */
input[type="range"]::-webkit-slider-thumb {
  appearance: none;
  height: 20px;
  width: 20px;
  border-radius: 50%;
  background: #3B82F6;
  cursor: pointer;
  box-shadow: 0 0 2px 0 #555;
}

input[type="range"]::-moz-range-thumb {
  height: 20px;
  width: 20px;
  border-radius: 50%;
  background: #3B82F6;
  cursor: pointer;
  border: none;
}
</style> 